import type { Vec3 } from './assemblyPose';
import { faceBasis, faceNormal, oppositeFace, pointOnFace, type FaceWord } from './partFaces';
import { inCategoryFamily, isHardwareOrTool, isLeftRight, type PartItem, type Side } from './partsRun';
import type { ByPart } from './partModelSize';
import {
  fastenerKind, hardwareReachOf, holeOrigins, holePlace, holeWidthOf, isCentred, type HoleAt, type StepHole,
} from './stepHoles';
import { builtBoundsOf } from './openaiBlenderApi';
import { pieceOf, type Job3D } from './threeDApi';
import type { Solid } from './partSolids';

// ONE PIECE OF HARDWARE, ONE STRAIGHT LINE — AND EVERY HOLE ON IT WORKED OUT, NOT ASKED.
//
// A screw through the rear frame into the end of a side rail makes two holes, and the barrel nut
// it threads into makes a third, across the rail. Asked hole by hole, the call placed each in its
// own part, and the two ends of one screw came out a few millimetres apart — two answers to what
// is one line. So the call now says where the hardware ENTERS (a part, a face, a place on it) and
// this file runs that line through the product:
//
// - THE PARTS ARE WHERE THE PRODUCT BUILT THEM. Each component's model is a piece cut out of the
//   kept whole product, and its measures are still in the product's frame (see builtBoundsOf) —
//   so the line from a face of one part meets the others where they really stand.
// - THE HOLES ARE SIZED BY THE HARDWARE. A screw reaches its own length from its head: through
//   every part it crosses whole, and into the last one as far as is left. A dowel sits half in
//   each of the two parts it joins. A barrel nut crosses the screw's line where the screw's tip
//   threads into it, and goes in as deep as its axis plus half its length.
//
// PLACES ON BOXES, DISTANCES ON THE WOOD. Where a hole is on a face is said on the part's box,
// as everywhere (see holePlace). How deep it goes is counted from where the line first meets the
// part's real surface — the drill counts from there too (drill_hole) — so every distance along a
// line is measured on the meshes (see partSolids): where the screw's head bears on the leg, where
// its shank reaches the rail. A part whose mesh did not load is measured on its box, and said.

// WHAT THE CALL ANSWERS PER LINE. `part`, `face` and the two figures are said as a hole is (see
// StepHole); `nut` is the hardware crossing the line, with the face of the part it goes in by.
export type HoleLine = {
  // THE ENTRY HOLE'S ID, where it was given before the call (see holeSlots); absent, numbered on.
  id?: string;
  at: string;
  via: string;
  part: string;
  side?: 'left' | 'right';
  face: FaceWord;
  u: HoleAt;
  v: HoleAt;
  nut?: { via: string; at: string; face: FaceWord } | null;
  // THE TABLE'S IDS FOR THE OTHER HOLES THIS LINE MAKES — where it runs on into the next part, the
  // nut's cross hole — by STORED part and hardware ids (see holeSlots). Absent on an answer's own
  // line, whose holes are numbered on.
  ids?: { part: string; via: string; id: string }[];
};

// A PART AS THE PRODUCT BUILT IT: its box in the product's frame, and which product run that is.
type Placed = { min: Vec3; max: Vec3; of: number };

export type LineEnv = {
  items: PartItem[];
  models: ByPart;
  // The component parts this line may cross — the hardware table row's.
  parts: string[];
  placed: Map<string, Placed>;
  // WHERE A MIRROR PAIR'S TWO UNITS REFLECT, across the product's width: only one unit is
  // modelled, so a line reaching the other one is reflected onto it.
  mirrorX: number | null;
  // Each part's real surface, where its mesh loaded.
  solids: Map<string, Solid>;
  // WHICH PARTS THE HARDWARE GOES ALL THE WAY THROUGH, as the hardware table says — and it is
  // the authority on it. Null where its row does not say (an answer from before it was asked):
  // then the line's own length decides.
  through: string[] | null;
};

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const scale = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const dot = (a: Vec3, b: Vec3) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const centre = (p: Placed): Vec3 => scale(add(p.min, p.max), 0.5);
const dims = (p: Placed) => ({ x: p.max[0] - p.min[0], y: p.max[1] - p.min[1], z: p.max[2] - p.min[2] });
const round = (mm: number) => Math.round(mm * 2) / 2;
// HOW MUCH AIR A LINE MAY CROSS BETWEEN TWO PARTS IT JOINS: none, bar what two meshes cut out of
// one product leave between surfaces that touch. More is hardware hanging between parts that do
// not meet where it was put — the entry placed wrong, or the parts' models.
const GAP_MM = 2;
// How long a box is along an axis-aligned direction.
// HOW FAR UNDER `face` OF `box` A POINT OF WOOD IS — a hole's StepHole.surfaceMm.
const under = (point: Vec3, face: FaceWord, box: Placed) => {
  const n = faceNormal(face);
  return round(Math.max(0, dot(centre(box), n) + spanOf(box, n) / 2 - dot(point, n)));
};

const spanOf = (box: Placed, a: Vec3) => {
  const s = dims(box);
  return Math.abs(a[0]) * s.x + Math.abs(a[1]) * s.y + Math.abs(a[2]) * s.z;
};

// EVERY COMPONENT'S PLACE, from its kept model — and the mirror plane, halfway across the parts
// that are not a pair (a frame, a seat: each symmetric about the product's middle). Components
// only: a piece of hardware is cut from its SET, whose frame is not the product's.
export function productMiddleX(
  items: PartItem[],
  meshOf: (id: string) => Job3D | null,
): number | null {
  const middles = items.flatMap((p, i) => {
    if (isHardwareOrTool(p) || isLeftRight(p)) return [];
    const b = builtBoundsOf(meshOf(String(i)));
    return b ? [(b.min[0] + b.max[0]) / 2] : [];
  }).sort((a, b) => a - b);
  return middles.length ? middles[Math.floor(middles.length / 2)] : null;
}

// WHICH HAND A PAIR'S KEPT FILE IS, by where the product built it: a person in front of the
// product has −x on their left. The other hand is that file reflected across `mirrorX`.
export const keptHand = (box: { min: Vec3; max: Vec3 }, mirrorX: number): Side =>
  ((box.min[0] + box.max[0]) / 2 < mirrorX ? 'left' : 'right');
const otherHand = (h: Side): Side => (h === 'left' ? 'right' : 'left');

export function lineEnv(
  items: PartItem[],
  models: ByPart,
  parts: string[],
  meshOf: (id: string) => Job3D | null,
  solids: Map<string, Solid> = new Map(),
  through: string[] | null = null,
): LineEnv {
  const placed = new Map<string, Placed>();
  items.forEach((_, i) => {
    const job = meshOf(String(i));
    const b = builtBoundsOf(job);
    const of = pieceOf(job)?.of;
    if (b && of != null) placed.set(String(i), { min: b.min, max: b.max, of });
  });
  return { items, models, parts, placed, mirrorX: productMiddleX(items, meshOf), solids, through };
}

// WHERE A RAY ENTERS AND LEAVES A BOX, as distances along it — or null where it misses.
function crossing(p: Vec3, d: Vec3, box: Placed): { enter: number; exit: number } | null {
  let enter = -Infinity;
  let exit = Infinity;
  for (let i = 0; i < 3; i++) {
    if (Math.abs(d[i]) < 1e-9) {
      if (p[i] < box.min[i] || p[i] > box.max[i]) return null;
      continue;
    }
    const a = (box.min[i] - p[i]) / d[i];
    const b = (box.max[i] - p[i]) / d[i];
    enter = Math.max(enter, Math.min(a, b));
    exit = Math.min(exit, Math.max(a, b));
  }
  return exit > enter && exit > 0.5 ? { enter, exit } : null;
}

// A POINT ON A FACE OF A BOX, said as a hole says it: millimetres from the face's own origin
// edges (see holeOrigins), clamped onto the face.
export function sayOn(point: Vec3, face: FaceWord, box: Placed): { u: HoleAt; v: HoleAt } {
  const { u, v } = faceBasis(face);
  const local = sub(point, centre(box));
  const at = (axis: Vec3) => round(Math.min(spanOf(box, axis), Math.max(0, dot(local, axis) + spanOf(box, axis) / 2)));
  const [uFrom, vFrom] = holeOrigins(face);
  return { u: { from: uFrom, mm: at(u) }, v: { from: vFrom, mm: at(v) } };
}

// The face of a box a ray going along `d` enters by: the one looking back at it.
const faceFacing = (d: Vec3): FaceWord =>
  (['left', 'right', 'front', 'back', 'bottom', 'top'] as FaceWord[])
    .find((f) => dot(faceNormal(f), d) < -0.99)!;

export const swapHand = (f: FaceWord): FaceWord => (f === 'left' || f === 'right' ? oppositeFace(f) : f);
// A place on a face measured from a left or right edge, measured from the other one's word.
export const swapFrom = (at: HoleAt): HoleAt => (isCentred(at) ? at : { ...at, from: swapHand(at.from) });

export type LineHoles = {
  holes: Omit<StepHole, 'id'>[];
  // Why the line stops short of what it was asked for — a part it cannot place, a length nobody
  // printed. Said, never filled in.
  notes: string[];
  // WHETHER THE LINE WORKS: on the wood, through what it must cross with no air between, and far
  // enough into the part it stops in. Null where it could not be checked (no place in the product,
  // no kind or length for the hardware). A line that does not is drilled nowhere (StepHole.unfit) —
  // and a place where it would not is not offered (see holeArea).
  fits: boolean | null;
  // THE PARTS WHOSE WOOD MEETS THE ENTRY PART'S along the line, where the entry part's wood ends the
  // way the hardware goes — within GAP_MM of it, or into it. Where two parts touch, on their meshes
  // and not their boxes: what a slot's outline is made of (see holeArea).
  touches: string[];
};

// THE HOLES ONE LINE MAKES, entry first.
//
// ON ONE UNIT, where it enters a mirror pair: `hand` says which, and every hole it makes says
// which unit it is in (StepHole.side). A pair takes one line from the call and two from here —
// the rail's two screws are two screws, each with its own holes, its own ids and its own notes.
// Every place is still said in the kept file's frame; the other hand's line is the kept one's
// reflected across the product's middle, so where it reaches a part that is NOT a pair (the seat
// both rails screw into) it reaches it at the reflected place, which is a different hole.
export function holesOfLine(
  line: HoleLine,
  env: LineEnv,
  name: (id: string) => string,
  hand?: Side,
): LineHoles {
  const { items, models } = env;
  const notes: string[] = [];
  const note = (n: string) => { if (!notes.includes(n)) notes.push(n); };
  // Wrong somewhere along it: said where, by its own note, and not drilled (see done).
  let fits = true;
  const fail = (n: string) => { fits = false; note(n); };
  let touches: string[] = [];
  const done = (holes: Omit<StepHole, 'id'>[]): LineHoles => {
    if (fits) return { holes, notes, fits, touches };
    if (!holes.some((h) => h.offWood)) note(`${name(line.via)} at the ${line.face} face of ${name(line.part)}: not drilled`);
    return { holes: holes.map((h) => (h.offWood ? h : { ...h, unfit: true as const })), notes, fits, touches };
  };
  const hole = (part: string, face: FaceWord, at: { u: HoleAt; v: HoleAt }, extra: Partial<StepHole>) => ({
    at: line.at, part, face, u: at.u, v: at.v, via: line.via,
    diameterMm: null, depthMm: null, through: false, ...extra,
  } as Omit<StepHole, 'id'>);
  const entrySide = hand ?? line.side;
  const entry = hole(line.part, line.face, { u: line.u, v: line.v }, {
    ...(entrySide ? { side: entrySide } : {}),
  });

  // WHERE A RAY GOES INTO A PART'S WOOD AND OUT OF IT, as distances along it: on the mesh, or on
  // the box where there is none (said). Null where it crosses the box and misses the wood.
  // `near`: how far behind `p` a surface still counts — a little, so a surface the line starts on is
  // met; further for a dowel's next part (see `end`).
  const wood = (id: string, box: Placed, p: Vec3, d: Vec3, near = -2.5): { enter: number; exit: number } | null => {
    const boxed = crossing(p, d, box);
    if (!boxed) return null;
    const solid = env.solids.get(id);
    if (!solid) {
      note(`${name(id)}: its model did not load, so the line is measured on its box`);
      return boxed;
    }
    const back = Math.max(5, 2.5 - near);
    const t = solid(add(p, scale(d, -back)), d).map((x) => x - back).filter((x) => x > near);
    return t.length ? { enter: t[0], exit: t[1] ?? boxed.exit } : null;
  };

  const airNote = (from: string, to: string, gap: number) =>
    `${name(line.via)} from ${name(from)} crosses ${round(gap)} mm of air before ${name(to)}: `
    + 'the parts do not meet along it';
  const kind = fastenerKind(items[Number(line.via)]);
  const length = hardwareReachOf(line.via, models);
  const from = env.placed.get(line.part);
  if (!from || !kind || !length) {
    if (!from) {
      note(`Not placed in the product: no piece of the Whole product model was matched to ${name(line.part)}, so `
        + 'where this hole goes, how deep it is and what it reaches cannot be checked — see Phase 5 › Whole product');
    }
    else if (!length) note(`${name(line.via)}: its 3D model has not been measured along its axis, so the line goes no further`);
    return { holes: [entry], notes, fits: null, touches };
  }
  const place = holePlace({ ...entry, id: '' } as StepHole, dims(from));
  const onFace = place && pointOnFace({ face: line.face, u: place.u, v: place.v }, dims(from));
  if (!onFace) {
    fits = false;
    return done([entry]);
  }
  const start = add(centre(from), onFace);
  const inward = scale(faceNormal(line.face), -1);
  // WHERE THE HARDWARE MEETS THE ENTRY PART'S WOOD: a screw's head bears there, a dowel's hole
  // starts there. Everything else along the line counts from it.
  const own = wood(line.part, from, start, inward);
  if (!own) {
    fail(`${name(line.via)} at ${entry.face} of ${name(line.part)}: that place is off the wood`);
    return done([{ ...entry, offWood: true }]);
  }
  // Its mouth, where the wood begins under the box face.
  const entered = { ...entry, surfaceMm: under(add(start, scale(inward, own.enter)), line.face, from) };
  // THE OTHER HAND'S LINE, where this one is it: measured on the kept file (the two are mirror
  // images, so where the head bears is the same), and searched for its other parts reflected.
  const mx = env.mirrorX;
  const reflect = !!hand && mx != null && hand !== keptHand(from, mx);
  const flipP = (p: Vec3): Vec3 => [2 * mx! - p[0], p[1], p[2]];
  const flipD = (d: Vec3): Vec3 => [-d[0], d[1], d[2]];

  // WHERE THE ENTRY PART'S WOOD ENDS, the way the hardware goes, as a distance along it from the
  // line's start on the box face: the wood's own surface for a dowel, which leaves the part by it
  // (behind the start, so negative), its far side for a screw, which goes on through.
  const end = kind === 'seated' ? -own.enter : own.exit;
  // THE OTHER PARTS OF THE ROW THE LINE CROSSES, nearest first — along it for a screw, back out
  // of the entry face for a dowel. A pair reached only by its other unit is reflected onto it,
  // and the hole it gets is in that unit.
  //
  // A DOWEL'S NEXT PART COUNTS FROM THE ENTRY PART'S WOOD, not from its box: two parts cut from one
  // model overlap boxes, and a frame whose surface lies 3 mm inside the rail's box face, flush with
  // the rail's end 3 mm inside it, is the rail's joint, not a part behind it. Looked for from there,
  // GAP_MM either side — a fixed 2.5 mm behind the box face found the frame where the rail's end sat
  // 2.4 mm in and lost it where it sat 2.6, and took its far face for its surface.
  const near = kind === 'seated' ? end - GAP_MM : -2.5;
  const along0 = kind === 'screw' ? inward : faceNormal(line.face);
  const p0 = reflect ? flipP(start) : start;
  const along = reflect ? flipD(along0) : along0;
  const hits = env.parts.filter((id) => id !== line.part).flatMap((id) => {
    const box = env.placed.get(id);
    if (!box) {
      note(`${name(id)}: no model cut from the whole product`);
      return [];
    }
    if (box.of !== from.of) {
      note(`${name(id)}: cut from another run than ${name(line.part)}`);
      return [];
    }
    const pair = isLeftRight(items[Number(id)]) && mx != null;
    const try1 = (p: Vec3, d: Vec3, mirrored: boolean) => {
      const w = wood(id, box, p, d, near);
      if (!w) return [];
      const side = pair ? (mirrored ? otherHand(keptHand(box, mx!)) : keptHand(box, mx!)) : undefined;
      return [{ id, box, p, d, mirrored, side, ...w }];
    };
    const direct = try1(p0, along, false);
    if (direct.length || !pair) return direct;
    return try1(flipP(p0), flipD(along), true);
  }).filter((h) => h.enter > (kind === 'seated' ? near : -0.5)).sort((a, b) => a.enter - b.enter);
  touches = hits.filter((h) => h.enter - end <= GAP_MM && h.exit > end).map((h) => h.id);

  const out: Omit<StepHole, 'id'>[] = [];
  if (kind === 'seated') {
    // HALF IN EACH: the entry part takes half the dowel from its surface, the part it joins the
    // rest, less the gap between the two surfaces.
    const next = hits[0];
    out.push({ ...entered, depthMm: round(length / 2) });
    if (next) {
      const face = faceFacing(next.d);
      const gap = next.enter + own.enter;
      if (gap > GAP_MM) fail(airNote(line.part, next.id, gap));
      const mouth = add(next.p, scale(next.d, next.enter));
      out.push(hole(next.id, face, sayOn(mouth, face, next.box), {
        depthMm: round(Math.max(1, length / 2 - gap)), surfaceMm: under(mouth, face, next.box),
        ...(next.side ? { side: next.side } : {}),
      }));
    } else {
      // A barrel nut has no other half: what went wrong is that it was sized as a dowel.
      fail(inCategoryFamily(items[Number(line.via)]?.category, new Set(['barrel nuts']))
        ? `${name(line.via)} was sized like a dowel: half its length (${round(length / 2)} mm) `
          + `instead of the full ${round(length)} mm`
        : `${name(line.via)}: nothing touches the ${line.face} face of ${name(line.part)}, `
          + 'so the other half has no hole');
    }
    return done(out);
  }

  // A SCREW, from its head: through what it crosses whole, into the last part as far as it
  // reaches — every distance counted from where the head bears.
  //
  // WHERE THE HARDWARE TABLE SAYS WHICH PARTS IT GOES THROUGH, THAT IS THE ANSWER: a part it
  // names is bored through, one it does not is where the screw stops, as deep as the screw has
  // length left. The geometry only places them — and where it disagrees (a screw too short to
  // reach the part the table puts its tip in), that is said with the numbers, not bored.
  const told = env.through;
  // Told: every part after one it goes through, up to the first it stops in.
  const tillStop = () => {
    const out: typeof hits = [];
    let open = told!.includes(line.part);
    for (const h of hits) {
      if (!open) break;
      out.push(h);
      open = told!.includes(h.id);
    }
    return out;
  };
  // AND THE HEAD SUNK AS DEEP AS IT MUST BE for the tip to bite into the part the table stops it
  // in: a third of its length, or half that part's wood along the line where that is less — so a
  // bite never comes out the far side. The call was asked this depth and was never told the two
  // distances it is made of; a 32 mm screw through a 44.5 mm rail answered 0. Too short even sunk
  // to the rail's far face: flush, and the note below says by how much.
  const stop = told ? tillStop().find((h) => !told.includes(h.id)) : undefined;
  const bite = stop ? Math.min(length / 3, (stop.exit - stop.enter) / 2) : 0;
  const deeper = stop ? round(Math.max(0, stop.enter + bite - length - own.enter)) : 0;
  const sunk = own.enter + deeper < own.exit ? deeper : 0;
  if (deeper && !sunk) {
    fail(`${name(line.via)} (${round(length)} mm) cannot reach ${name(stop!.id)} even with its head sunk `
      + `through ${name(line.part)}`);
  }
  // The hole's first stretch is empty, and the screw starts below it.
  const s0 = own.enter + sunk;
  const reached = told ? tillStop() : hits.filter((h) => h.enter - s0 < length - 0.5);
  const goesThrough = (id: string, fallback: boolean) => (told ? told.includes(id) : fallback);
  const entryThrough = goesThrough(line.part, reached.length > 0);
  out.push({
    ...entered,
    ...(sunk ? { sunkMm: sunk } : {}),
    through: entryThrough,
    depthMm: entryThrough ? null : round(length),
    ...(entryThrough ? { woodMm: round(own.exit - own.enter) } : {}),
  });
  reached.forEach((h, k) => {
    const face = faceFacing(h.d);
    const last = k === reached.length - 1;
    const through = goesThrough(h.id, !last || h.exit - s0 <= length);
    const left = length - (h.enter - s0);
    if (!through && left < 1) {
      // SAID FROM WHERE THE HEAD SITS, the one place on the line a person can point at: the
      // distances along it start on the part's box face, which nobody sees.
      fail(`${name(line.via)} (${round(length)} mm) is ${round(-left)} mm too short to reach ${name(h.id)}: `
        + `from where its head sits on ${name(line.part)}, ${name(h.id)} is ${round(h.enter - s0)} mm away`);
      return;
    }
    const mouth = add(h.p, scale(h.d, h.enter));
    out.push(hole(h.id, face, sayOn(mouth, face, h.box), {
      through, depthMm: through ? null : round(left), surfaceMm: under(mouth, face, h.box),
      ...(through ? { woodMm: round(h.exit - h.enter) } : {}),
      ...(h.side ? { side: h.side } : {}),
    }));
  });
  if (!reached.length && env.parts.length > 1) {
    fail(`${name(line.via)} from ${name(line.part)} reaches no other part of its row`);
  }
  // AND NO AIR BETWEEN THE PARTS IT JOINS: from where it leaves one part's wood to where it enters
  // the next's. The line reaching the next part is not enough — a screw passing over a rail's end
  // and into the rail where it rises again reached it, through a gap nothing could be screwed across.
  reached.reduce((prev, h) => {
    if (h.enter - prev.exit > GAP_MM) fail(airNote(prev.id, h.id, h.enter - prev.exit));
    return { id: h.id, exit: h.exit };
  }, { id: line.part, exit: own.exit });

  // THE NUT ACROSS THE LINE, in the part the tip is in: its axis through the line half a nut
  // short of the screw's tip, entering by the face the call named, as deep from that face's wood
  // as the line plus half the nut.
  const tip = reached[reached.length - 1];
  if (line.nut && tip) {
    // Said in the kept frame's words: reflected once (this hand, or the tip's unit), the hands swap.
    const face = reflect !== tip.mirrored ? swapHand(line.nut.face) : line.nut.face;
    const n = faceNormal(face);
    const wide = holeWidthOf({ via: line.nut.via } as StepHole, items, models) ?? 0;
    const nutLength = hardwareReachOf(line.nut.via, models);
    if (Math.abs(dot(n, tip.d)) > 0.01) {
      note(`${name(line.nut.via)}: the ${line.nut.face} face is not across the screw`);
    } else {
      const t = Math.min(s0 + length - wide / 2, tip.exit - wide / 2);
      const axis = add(tip.p, scale(tip.d, t));
      // From outside the face, back toward the line: the first wood met is where the nut goes in.
      const reach = dot(centre(tip.box), n) + spanOf(tip.box, n) / 2 - dot(axis, n) + 5;
      const surface = wood(tip.id, tip.box, add(axis, scale(n, reach)), scale(n, -1));
      if (!surface) {
        note(`${name(line.nut.via)}: no wood of ${name(tip.id)} on its ${line.nut.face} face there`);
      } else {
        out.push({
          ...hole(tip.id, face, sayOn(axis, face, tip.box), tip.side ? { side: tip.side } : {}),
          at: line.nut.at,
          via: line.nut.via,
          depthMm: nutLength ? round(reach - surface.enter + nutLength / 2) : null,
          surfaceMm: under(add(axis, scale(n, reach - surface.enter)), face, tip.box),
        });
      }
    }
  }
  return done(out);
}
